// src/button.tsx
import React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
var base = "inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-kyc-info disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.97] whitespace-nowrap";
var variants = {
  primary: "bg-kyc-primary text-white hover:bg-kyc-primary-light shadow-sm dark:bg-gray-700 dark:hover:bg-gray-600",
  secondary: "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700",
  danger: "bg-kyc-danger text-white hover:bg-red-600 shadow-sm"
};
var sizes = {
  sm: "px-3 py-1.5 text-xs",
  md: "px-4 py-2 text-sm",
  lg: "px-6 py-3 text-base"
};
var Button = React.forwardRef(
  ({
    variant = "primary",
    size = "md",
    loading,
    className = "",
    children,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "button",
      {
        ref,
        className: `${base} ${variants[variant]} ${sizes[size]} ${className}`,
        disabled: loading || props.disabled,
        ...props,
        children: [
          loading && /* @__PURE__ */ jsx("span", { className: "w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin shrink-0" }),
          /* @__PURE__ */ jsx("span", { className: "flex items-center justify-center", children })
        ]
      }
    );
  }
);
Button.displayName = "Button";

// src/progress-indicator.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var ProgressIndicator = ({
  current,
  total,
  currentStep = ""
}) => {
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      className: "flex flex-col gap-2",
      role: "progressbar",
      "aria-valuenow": current,
      "aria-valuemin": 0,
      "aria-valuemax": total,
      "aria-label": `Step ${current} of ${total}${currentStep ? `: ${currentStep}` : ""}`,
      children: [
        /* @__PURE__ */ jsx2("div", { className: "flex gap-1.5", children: Array.from({ length: total }, (_, i) => {
          const stepNum = i + 1;
          const isComplete = stepNum < current;
          const isCurrent = stepNum === current;
          return /* @__PURE__ */ jsx2(
            "span",
            {
              className: `h-1.5 flex-1 rounded-full transition-colors duration-300 ${isComplete ? "bg-kyc-success" : isCurrent ? "bg-kyc-info animate-pulse" : "bg-gray-200 dark:bg-gray-700"}`
            },
            i
          );
        }) }),
        /* @__PURE__ */ jsxs2("div", { className: "flex justify-between items-baseline text-xs", children: [
          /* @__PURE__ */ jsxs2("span", { className: "font-medium text-gray-500 dark:text-gray-400 tabular-nums", children: [
            "Step ",
            current,
            " of ",
            total
          ] }),
          currentStep && /* @__PURE__ */ jsx2("span", { className: "font-semibold text-gray-900 capitalize dark:text-gray-100", children: currentStep })
        ] })
      ]
    }
  );
};
ProgressIndicator.displayName = "ProgressIndicator";

// src/overlay.tsx
import React2 from "react";
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
function DirectionChevron({
  pose,
  cx,
  cy,
  rx,
  ry
}) {
  const s = 14;
  let points = "";
  let x = cx;
  let y = cy;
  let animClass = "";
  if (pose === "left") {
    x = cx - rx + s + 18;
    points = `${x + s},${y - s} ${x - s},${y} ${x + s},${y + s}`;
    animClass = "animate-kyc-nudge-left";
  } else if (pose === "right") {
    x = cx + rx - s - 18;
    points = `${x - s},${y - s} ${x + s},${y} ${x - s},${y + s}`;
    animClass = "animate-kyc-nudge-right";
  } else {
    y = cy - ry + s + 18;
    points = `${x - s},${y + s} ${x},${y - s} ${x + s},${y + s}`;
    animClass = "animate-kyc-nudge-up";
  }
  return /* @__PURE__ */ jsx3(
    "polyline",
    {
      points,
      fill: "none",
      stroke: "white",
      strokeWidth: 3.5,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className: animClass,
      style: { filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.6))" }
    }
  );
}
var Overlay = ({
  isDetected,
  isAligned,
  guidance = "",
  width = 360,
  height = 360,
  currentPose
}) => {
  const maskId = React2.useId();
  const cx = width / 2;
  const cy = height / 2;
  const rx = Math.min(width * 0.36, height * 0.33);
  const ry = Math.min(height * 0.44, rx * 1.32);
  const ringStroke = isAligned ? "#10b981" : isDetected ? "#ffffff" : "rgba(255,255,255,0.55)";
  const ringWidth = isDetected || isAligned ? 3.5 : 2.5;
  return /* @__PURE__ */ jsxs3("div", { className: "relative w-full h-full", children: [
    /* @__PURE__ */ jsxs3(
      "svg",
      {
        viewBox: `0 0 ${width} ${height}`,
        className: "block w-full h-full",
        preserveAspectRatio: "xMidYMid slice",
        "aria-hidden": true,
        children: [
          /* @__PURE__ */ jsx3("defs", { children: /* @__PURE__ */ jsxs3("mask", { id: maskId, children: [
            /* @__PURE__ */ jsx3("rect", { x: 0, y: 0, width, height, fill: "white" }),
            /* @__PURE__ */ jsx3("ellipse", { cx, cy, rx, ry, fill: "black" })
          ] }) }),
          /* @__PURE__ */ jsx3(
            "rect",
            {
              x: 0,
              y: 0,
              width,
              height,
              fill: "rgba(10, 15, 26, 0.55)",
              mask: `url(#${maskId})`,
              style: { transition: "fill 0.3s ease" }
            }
          ),
          /* @__PURE__ */ jsx3(
            "ellipse",
            {
              cx,
              cy,
              rx,
              ry,
              fill: "none",
              stroke: ringStroke,
              strokeWidth: ringWidth,
              style: {
                transition: "stroke 0.3s ease, stroke-width 0.3s ease",
                filter: isAligned ? "drop-shadow(0 0 10px rgba(16,185,129,0.6))" : isDetected ? "drop-shadow(0 0 6px rgba(255,255,255,0.35))" : "none"
              },
              className: isAligned ? "animate-kyc-pulse-ring" : void 0
            }
          ),
          (currentPose === "left" || currentPose === "right" || currentPose === "up") && /* @__PURE__ */ jsx3(DirectionChevron, { pose: currentPose, cx, cy, rx, ry })
        ]
      }
    ),
    /* @__PURE__ */ jsx3("div", { className: "absolute top-3 inset-x-0 flex justify-center", children: /* @__PURE__ */ jsxs3(
      "span",
      {
        className: "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium\n                     bg-slate-900/70 text-white backdrop-blur-sm",
        role: "status",
        children: [
          /* @__PURE__ */ jsx3(
            "span",
            {
              className: `w-1.5 h-1.5 rounded-full transition-colors duration-300 ${isDetected ? "bg-emerald-400" : "bg-amber-400 animate-pulse"}`,
              "aria-hidden": true
            }
          ),
          isDetected ? "Face detected" : "Looking for your face\u2026"
        ]
      }
    ) }),
    guidance && /* @__PURE__ */ jsx3("div", { className: "absolute bottom-4 inset-x-4 flex justify-center", children: /* @__PURE__ */ jsx3(
      "p",
      {
        className: "px-4 py-2.5 rounded-xl bg-slate-900/75 backdrop-blur-sm text-white\n                       text-sm font-medium text-center leading-snug max-w-[90%] shadow-lg",
        "aria-live": "polite",
        children: guidance
      }
    ) })
  ] });
};
Overlay.displayName = "Overlay";

// src/instruction-text.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "react/jsx-runtime";
var typeStyles = {
  info: {
    wrapper: "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
    title: "text-blue-900 dark:text-blue-200",
    message: "text-blue-800 dark:text-blue-300",
    sub: "text-blue-600 dark:text-blue-400",
    border: "border-l-kyc-info",
    icon: /* @__PURE__ */ jsxs4("svg", { width: 16, height: 16, viewBox: "0 0 16 16", fill: "none", "aria-hidden": true, children: [
      /* @__PURE__ */ jsx4("circle", { cx: 8, cy: 8, r: 7, stroke: "currentColor", strokeWidth: 1.5 }),
      /* @__PURE__ */ jsx4("path", { d: "M8 5h.01M8 7.5v4", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round" })
    ] })
  },
  success: {
    wrapper: "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800",
    title: "text-emerald-900 dark:text-emerald-200",
    message: "text-emerald-800 dark:text-emerald-300",
    sub: "text-emerald-600 dark:text-emerald-400",
    border: "border-l-kyc-success",
    icon: /* @__PURE__ */ jsxs4("svg", { width: 16, height: 16, viewBox: "0 0 16 16", fill: "none", "aria-hidden": true, children: [
      /* @__PURE__ */ jsx4("circle", { cx: 8, cy: 8, r: 7, stroke: "currentColor", strokeWidth: 1.5 }),
      /* @__PURE__ */ jsx4("path", { d: "M4.5 8l2.5 2.5 4.5-5", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" })
    ] })
  },
  warning: {
    wrapper: "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
    title: "text-amber-900 dark:text-amber-200",
    message: "text-amber-800 dark:text-amber-300",
    sub: "text-amber-600 dark:text-amber-400",
    border: "border-l-kyc-warning",
    icon: /* @__PURE__ */ jsxs4("svg", { width: 16, height: 16, viewBox: "0 0 16 16", fill: "none", "aria-hidden": true, children: [
      /* @__PURE__ */ jsx4("path", { d: "M8 1.5L14.5 13H1.5L8 1.5z", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" }),
      /* @__PURE__ */ jsx4("path", { d: "M8 6v3.5M8 11h.01", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round" })
    ] })
  },
  error: {
    wrapper: "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800",
    title: "text-red-900 dark:text-red-200",
    message: "text-red-800 dark:text-red-300",
    sub: "text-red-600 dark:text-red-400",
    border: "border-l-kyc-danger",
    icon: /* @__PURE__ */ jsxs4("svg", { width: 16, height: 16, viewBox: "0 0 16 16", fill: "none", "aria-hidden": true, children: [
      /* @__PURE__ */ jsx4("circle", { cx: 8, cy: 8, r: 7, stroke: "currentColor", strokeWidth: 1.5 }),
      /* @__PURE__ */ jsx4("path", { d: "M5.5 5.5l5 5M10.5 5.5l-5 5", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round" })
    ] })
  }
};
var InstructionText = ({
  title,
  message,
  subMessage,
  type = "info"
}) => {
  const s = typeStyles[type];
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      className: `flex gap-3 p-4 rounded-xl border border-l-4 ${s.border} ${s.wrapper} animate-kyc-fadein`,
      role: "status",
      children: [
        /* @__PURE__ */ jsx4("span", { className: `shrink-0 mt-0.5 ${s.title}`, children: s.icon }),
        /* @__PURE__ */ jsxs4("div", { className: "min-w-0 flex-1", children: [
          title && /* @__PURE__ */ jsx4("p", { className: `text-sm font-semibold mb-1 ${s.title}`, children: title }),
          /* @__PURE__ */ jsx4("p", { className: `text-sm leading-relaxed ${s.message}`, children: message }),
          subMessage && /* @__PURE__ */ jsx4("p", { className: `text-xs mt-1.5 leading-relaxed ${s.sub}`, children: subMessage })
        ] })
      ]
    }
  );
};
InstructionText.displayName = "InstructionText";

// src/views.tsx
import { jsx as jsx5, jsxs as jsxs5 } from "react/jsx-runtime";
var ErrorView = ({ message, code, onRetry }) => {
  return /* @__PURE__ */ jsxs5("div", { className: "flex flex-col items-center justify-center px-5 py-10 text-center min-h-[280px] animate-kyc-fadein", children: [
    /* @__PURE__ */ jsx5("span", { className: "inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 text-kyc-danger mb-4 dark:bg-red-950/40 animate-kyc-shake", children: /* @__PURE__ */ jsxs5("svg", { width: 28, height: 28, viewBox: "0 0 28 28", fill: "none", "aria-hidden": true, children: [
      /* @__PURE__ */ jsx5("circle", { cx: 14, cy: 14, r: 12, stroke: "currentColor", strokeWidth: 2 }),
      /* @__PURE__ */ jsx5("path", { d: "M14 9v6M14 17.5h.01", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" })
    ] }) }),
    /* @__PURE__ */ jsx5("h2", { className: "text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1", children: "Verification Failed" }),
    /* @__PURE__ */ jsx5("p", { className: "text-sm text-gray-600 dark:text-gray-400 leading-relaxed max-w-xs mb-2", children: message }),
    code && /* @__PURE__ */ jsx5("p", { className: "text-xs font-mono text-gray-400 dark:text-gray-500 mb-4", children: code }),
    onRetry && /* @__PURE__ */ jsx5(Button, { onClick: onRetry, variant: "primary", className: "mt-2", children: "Try Again" })
  ] });
};
ErrorView.displayName = "ErrorView";
var LoadingView = ({ message = "Processing..." }) => {
  return /* @__PURE__ */ jsxs5("div", { className: "flex flex-col items-center justify-center px-5 py-10 text-center min-h-[280px] animate-kyc-fadein", children: [
    /* @__PURE__ */ jsx5(
      "span",
      {
        className: "w-10 h-10 rounded-full border-4 border-gray-200 border-t-kyc-info animate-spin mb-4 dark:border-gray-700 dark:border-t-kyc-info",
        role: "status",
        "aria-label": "Loading"
      }
    ),
    /* @__PURE__ */ jsx5("p", { className: "text-sm font-medium text-gray-600 dark:text-gray-400", children: message })
  ] });
};
LoadingView.displayName = "LoadingView";
export {
  Button,
  ErrorView,
  InstructionText,
  LoadingView,
  Overlay,
  ProgressIndicator
};
